import React, { useEffect, useMemo, useRef, useState } from "react";
import ForceGraph2D from "react-force-graph-2d";

// A cinematic upgrade to AVA's Living Knowledge Graph - the small 2D force graph
// stays underneath (because we still need react-force-graph's physics simulation
// for the actual synaptic layout), but the visual language around it goes deep:
//
//   • Ambient starfield behind the graph (drawn onto the canvas itself so it
//     tumbles with the pan/zoom, not floating in front of it).
//   • Node radius, glow intensity, and pulsing amplitude ALL scale with the
//     node's activation count - high-activation "hub" concepts breathe brighter
//     and larger, low-activation ones fade to a whisper.
//   • Edge width, opacity, and the moving Hebbian pulse dot ALL scale with the
//     edge's strength - strongly reinforced synapses light up as bright chords.
//   • Ambient consciousness glow pulse around the entire canvas centre.

function nodeSourceColor(node) {
  // Soft, cool "ice-glacier" cortex palette - low-saturation glacial tones that
  // glow like bioluminescent neural tissue rather than electric neon.
  const tags = node.source_projects || [];
  if (tags.length === 0) return { rgba: "rgba(143,208,214,0.72)", hex: "#8FD0D6" }; // glacier ice-teal - self/associative
  const gitTags = tags.filter((t) => t !== "web-search");
  if (gitTags.length > 0) return { rgba: "rgba(147,180,222,0.72)", hex: "#93B4DE" }; // glacier blue - ship-taught
  return { rgba: "rgba(174,168,224,0.72)", hex: "#AEA8E0" }; // cool periwinkle - web research
}

// Simple deterministic pseudo-random starfield - 90 tiny stars scattered across a
// wide canvas box, painted directly by the graph's canvas layer so they inherit
// pan/zoom transforms naturally.
function useStarfield(count = 90) {
  return useMemo(() => {
    const stars = [];
    let seed = 42;
    const rnd = () => { seed = (seed * 9301 + 49297) % 233280; return seed / 233280; };
    for (let i = 0; i < count; i++) {
      stars.push({ x: (rnd() - 0.5) * 1200, y: (rnd() - 0.5) * 900, r: 0.15 + rnd() * 0.5, tw: rnd() * Math.PI * 2 });
    }
    return stars;
  }, [count]);
}

export default function KnowledgeGraph({ data }) {
  const containerRef = useRef(null);
  const fgRef = useRef(null);
  const [dims, setDims] = useState({ width: 300, height: 300 });
  const stars = useStarfield();

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const observer = new ResizeObserver((entries) => {
      const { width, height } = entries[0].contentRect;
      setDims({ width, height });
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // Pre-compute the maximum activation count in the graph so we can normalise
  // node radius and pulse amplitude around the actual data range rather than a
  // hard-coded ceiling - this way even a young graph reads cleanly.
  const maxVal = useMemo(() => {
    if (!data || !data.nodes) return 1;
    let m = 1;
    for (const n of data.nodes) m = Math.max(m, n.val || 1);
    return m;
  }, [data]);

  if (!data || data.nodes.length === 0) {
    return (
      <div ref={containerRef} className="relative w-full h-full flex items-center justify-center">
        {/* Even in the dormant state we render an ambient breathing halo so the
            panel doesn't look dead - signals to Doc that the graph is READY,
            just waiting for its first exchange. */}
        <div
          className="absolute w-40 h-40 rounded-full pointer-events-none"
          style={{ background: "radial-gradient(circle, rgba(110,180,188,0.14), transparent 65%)", filter: "blur(10px)", animation: "pulse 4s ease-in-out infinite" }}
        />
        <p className="relative text-[11px] text-slate-500 font-mono-data text-center px-4 leading-relaxed">
          Synaptic space quiet.<br />
          <span className="text-slate-600">Engage AVA to trigger first plasticity.</span>
        </p>
      </div>
    );
  }

  return (
    <div ref={containerRef} className="relative w-full h-full" data-testid="knowledge-graph-canvas">
      {/* Ambient consciousness glow - a soft radial bloom under the graph that
          breathes gently, gives the whole panel a "living" quality without ever
          drawing the eye away from the actual nodes. */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background: "radial-gradient(ellipse 55% 60% at 50% 50%, rgba(110,180,188,0.06), transparent 62%)",
          filter: "blur(8px)",
          animation: "kg-glow 4.8s ease-in-out infinite",
        }}
      />
      <style>{`
        @keyframes kg-glow { 0%,100% { opacity: 0.55; } 50% { opacity: 1; } }
      `}</style>
      <ForceGraph2D
        ref={fgRef}
        width={dims.width}
        height={dims.height}
        graphData={data}
        backgroundColor="rgba(0,0,0,0)"
        nodeLabel="label"
        nodeRelSize={4}
        d3AlphaDecay={0.02}
        d3VelocityDecay={0.32}
        linkWidth={(link) => 0.5 + link.value * 2.4}
        linkColor={(link) => `rgba(134,197,201,${0.07 + link.value * 0.34})`}
        // Draw the ambient starfield BEFORE the physics graph paints, so stars
        // sit behind every node and edge but still respond to zoom/pan.
        onRenderFramePre={(ctx) => {
          const t = Date.now() / 900;
          for (const s of stars) {
            const tw = 0.28 + 0.28 * Math.sin(t + s.tw);
            ctx.beginPath();
            ctx.arc(s.x, s.y, s.r, 0, 2 * Math.PI);
            ctx.fillStyle = `rgba(176,200,205,${tw})`;
            ctx.fill();
          }
        }}
        // A travelling Hebbian pulse dot rides along every edge - dot brightness
        // and speed both scale with edge strength, giving strongly-reinforced
        // synapses noticeably faster and brighter signal traffic.
        linkCanvasObjectMode={() => "after"}
        linkCanvasObject={(link, ctx) => {
          const { source, target, value } = link;
          if (!source || !target) return;
          if (typeof source.x !== "number" || typeof target.x !== "number") return;
          if (!isFinite(source.x) || !isFinite(source.y) || !isFinite(target.x) || !isFinite(target.y)) return;
          const speed = 0.4 + value * 1.6;
          const p = ((Date.now() / 1000) * speed + (source.index || 0) * 0.11) % 1;
          const x = source.x + (target.x - source.x) * p;
          const y = source.y + (target.y - source.y) * p;
          ctx.beginPath();
          ctx.arc(x, y, 0.9 + value * 0.9, 0, 2 * Math.PI);
          ctx.fillStyle = `rgba(205,230,229,${0.25 + value * 0.4})`;
          ctx.shadowColor = "#86C5C9";
          ctx.shadowBlur = 4;
          ctx.fill();
          ctx.shadowBlur = 0;
        }}
        nodeColor={(node) => nodeSourceColor(node).hex}
        nodeCanvasObject={(node, ctx, globalScale) => {
          if (typeof node.x !== "number" || typeof node.y !== "number" || !isFinite(node.x) || !isFinite(node.y)) return;
          const color = nodeSourceColor(node);
          const norm = Math.min(1, (node.val || 1) / (maxVal || 1));
          // Radius, glow, and pulse amplitude all scale with activation count.
          const r = 2.6 + norm * 5.2;
          const pulseAmp = 0.12 + norm * 0.28;
          const pulse = 1 + pulseAmp * Math.sin(Date.now() / 620 + (node.x || 0));
          // Outer diffuse halo - subtle, soft bloom that gives hub concepts weight
          // without the harsh neon ring the old palette had.
          const grad = ctx.createRadialGradient(node.x, node.y, 0, node.x, node.y, r * pulse * 3.2);
          grad.addColorStop(0, color.rgba.replace(/[\d.]+\)$/, `${0.2 + norm * 0.3})`));
          grad.addColorStop(1, "rgba(0,0,0,0)");
          ctx.beginPath();
          ctx.arc(node.x, node.y, r * pulse * 3.2, 0, 2 * Math.PI);
          ctx.fillStyle = grad;
          ctx.fill();
          // Core dot with soft glow.
          ctx.beginPath();
          ctx.arc(node.x, node.y, r * pulse, 0, 2 * Math.PI);
          ctx.fillStyle = color.rgba;
          ctx.shadowColor = color.hex;
          ctx.shadowBlur = 6 + norm * 5;
          ctx.fill();
          ctx.shadowBlur = 0;
          // Gentle inner highlight - a soft warm-white core, not a hard white pinpoint.
          ctx.beginPath();
          ctx.arc(node.x, node.y, r * pulse * 0.3, 0, 2 * Math.PI);
          ctx.fillStyle = "rgba(232,244,246,0.55)";
          ctx.fill();
          if (globalScale > 1.15) {
            ctx.font = "3.5px JetBrains Mono";
            ctx.fillStyle = "rgba(226,232,240,0.85)";
            ctx.textAlign = "center";
            ctx.fillText(node.label, node.x, node.y + r * pulse + 5);
          }
        }}
        cooldownTicks={200}
      />
    </div>
  );
}
